import React, { useMemo, useState } from "react"
import {
    ThumbsUp, ThumbsDown, Lightbulb, Bug, Swords, ShoppingCart, Quote, Heart, MessageCircle,
    Eye, ExternalLink, Layers, ChevronDown, ChevronUp, Film, Sparkles, Gauge, Info,
} from "lucide-react"
import { SENTIMENT_META, SENTIMENT_ORDER, getPlatformMeta } from "../../constants"
import { formatViews } from "../../utils"
import {
    Card, SectionTitle, SentimentChip, SentimentBar, SentimentLegend, NetScore, PurchaseIntentMeter, PlatformBadge,
} from "./SentimentParts"
import {
    normalizeInsightList, extractComments, buildVideoIndex, formatSentimentDate, hasAnyCounts,
} from "./sentimentUtils"

const SELECTED_AS_LABEL = {
    most_viewed: "Most viewed",
    top_performer: "Top performer",
    recent_top: "Recent top performer",
    recent_top_performer: "Recent top performer",
    both: "Most viewed & top performer",
}
const formatSelectedAs = (value) =>
    SELECTED_AS_LABEL[value] || (value ? String(value).replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase()) : null)

const INSIGHT_GROUPS = [
    { key: "praise", title: "Praise", icon: ThumbsUp, color: "#10b981", empty: "No recurring praise" },
    { key: "complaints", title: "Complaints", icon: ThumbsDown, color: "#ef4444", empty: "No recurring complaints" },
    { key: "feature_requests", title: "Feature requests", icon: Lightbulb, color: "#3b82f6", empty: "No feature requests" },
    { key: "bugs_or_issues", title: "Bugs & issues", icon: Bug, color: "#f59e0b", empty: "No bugs reported" },
]

const COMMENTS_PAGE = 8

const Likes = ({ value }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#9ca3af", fontSize: "12px" }}>
        <Heart size={12} /> {formatViews(value || 0)}
    </span>
)

const VideoRef = ({ video, platform }) => {
    if (!video?.url) return null
    return (
        <a href={video.url} target="_blank" rel="noopener noreferrer" className="ugc-sent-videoref" title={video.title || ""}>
            <Film size={11} /> {video.title || `View on ${getPlatformMeta(platform).label}`}
        </a>
    )
}

const ThemeCard = ({ theme, maxMentions, videoIndex, platform }) => {
    const [open, setOpen] = useState(false)
    const meta = SENTIMENT_META[theme.sentiment] || SENTIMENT_META.unknown
    const examples = Array.isArray(theme.examples) ? theme.examples : []
    const share = maxMentions > 0 ? Math.max(4, ((Number(theme.mentions) || 0) / maxMentions) * 100) : 0

    return (
        <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "14px 16px" }}>
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <span style={{ color: "#fff", fontWeight: 600, fontSize: "14.5px" }}>{theme.theme}</span>
                        <SentimentChip sentiment={theme.sentiment} size="sm" />
                    </div>
                    {theme.description && (
                        <p style={{ color: "#adb5bd", fontSize: "13px", lineHeight: 1.6, margin: 0 }}>{theme.description}</p>
                    )}
                </div>
                {theme.mentions != null && (
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: "18px", lineHeight: 1 }}>{theme.mentions}</div>
                        <div style={{ color: "#6b7280", fontSize: "11px" }}>mentions</div>
                    </div>
                )}
            </div>
            {theme.mentions != null && (
                <div style={{ height: "4px", background: "rgba(255,255,255,0.05)", borderRadius: "2px", marginTop: "12px" }}>
                    <div style={{ width: `${share}%`, height: "100%", background: meta.color, borderRadius: "2px" }} />
                </div>
            )}
            {examples.length > 0 && (
                <>
                    <button type="button" onClick={() => setOpen(v => !v)} className="ugc-sent-link mt-2">
                        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        {open ? "Hide" : "Show"} {examples.length} example{examples.length === 1 ? "" : "s"}
                    </button>
                    {open && (
                        <div className="d-flex flex-column gap-2 mt-2">
                            {examples.map((ex, i) => (
                                <div key={i} style={{ borderLeft: `2px solid ${(SENTIMENT_META[ex.sentiment] || meta).color}`, padding: "4px 0 4px 12px" }}>
                                    <p style={{ color: "#d1d5db", fontSize: "13px", lineHeight: 1.55, margin: "0 0 4px" }}>“{ex.text}”</p>
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                        <Likes value={ex.likes} />
                                        <VideoRef video={videoIndex[ex.video_id]} platform={platform} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

const InsightGroup = ({ group, items }) => {
    const Icon = group.icon
    return (
        <Card style={{ padding: "16px" }}>
            <div className="d-flex align-items-center gap-2 mb-3">
                <span style={{ width: "28px", height: "28px", borderRadius: "8px", background: `${group.color}18`, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={14} color={group.color} />
                </span>
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>{group.title}</span>
                {items.length > 0 && <span style={{ color: "#6b7280", fontSize: "12px" }}>{items.length}</span>}
            </div>
            {items.length === 0 ? (
                <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>{group.empty}</p>
            ) : (
                <ul className="ugc-sent-insight-list">
                    {items.map((item, i) => (
                        <li key={i}>
                            <span style={{ color: "#e8e8e8" }}>{item.text}</span>
                            {item.mentions != null && <span style={{ color: "#6b7280", fontSize: "12px" }}> · {item.mentions} mentions</span>}
                            {item.detail && <div style={{ color: "#9ca3af", fontSize: "12.5px", marginTop: "2px" }}>{item.detail}</div>}
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    )
}

const CommentsExplorer = ({ comments, videoIndex, platform }) => {
    const [filter, setFilter] = useState("all")
    const [visible, setVisible] = useState(COMMENTS_PAGE)

    const counts = useMemo(() => {
        const c = { all: comments.length }
        comments.forEach(cm => { c[cm.sentiment] = (c[cm.sentiment] || 0) + 1 })
        return c
    }, [comments])

    const filtered = filter === "all" ? comments : comments.filter(c => c.sentiment === filter)
    const options = ["all", ...SENTIMENT_ORDER.filter(key => counts[key])]

    return (
        <div>
            <div className="d-flex gap-2 flex-wrap mb-3">
                {options.map(key => {
                    const isActive = key === filter
                    const meta = SENTIMENT_META[key]
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => { setFilter(key); setVisible(COMMENTS_PAGE) }}
                            className="ugc-sent-filter"
                            style={isActive ? { background: "rgba(246,37,90,0.1)", borderColor: "rgba(246,37,90,0.45)", color: "#fff" } : undefined}
                        >
                            {meta && <span style={{ width: 7, height: 7, borderRadius: "50%", background: meta.color }} />}
                            {meta ? meta.label : "All"}
                            <span style={{ color: "#6b7280" }}>{counts[key] || 0}</span>
                        </button>
                    )
                })}
            </div>
            <div className="d-flex flex-column gap-2">
                {filtered.slice(0, visible).map((comment, i) => (
                    <div key={i} className="ugc-sent-comment">
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: (SENTIMENT_META[comment.sentiment] || SENTIMENT_META.unknown).color, flexShrink: 0, marginTop: "7px" }}
                            title={(SENTIMENT_META[comment.sentiment] || SENTIMENT_META.unknown).label} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <p style={{ color: "#d1d5db", fontSize: "13.5px", lineHeight: 1.55, margin: "0 0 6px", wordBreak: "break-word" }}>{comment.text}</p>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                {comment.author && <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 600 }}>{comment.author}</span>}
                                <Likes value={comment.likes} />
                                {comment.replies > 0 && (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#9ca3af", fontSize: "12px" }}>
                                        <MessageCircle size={12} /> {comment.replies}
                                    </span>
                                )}
                                <SentimentChip sentiment={comment.sentiment} size="sm" />
                                <VideoRef video={videoIndex[comment.video_id]} platform={platform} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {filtered.length > COMMENTS_PAGE && (
                <div className="d-flex justify-content-center gap-2 mt-3">
                    {filtered.length > visible && (
                        <button type="button" className="ugc-btn-ghost" onClick={() => setVisible(v => v + COMMENTS_PAGE * 2)}>
                            Show more ({filtered.length - visible} left)
                        </button>
                    )}
                    {visible > COMMENTS_PAGE && (
                        <button type="button" className="ugc-btn-ghost" onClick={() => setVisible(COMMENTS_PAGE)}>
                            Show less
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

/**
 * Deep dive for one completed platform: AI summary, themes, praise /
 * complaints / requests / bugs, notable quotes, the analysed comments, and a
 * sidebar with the (raw or like-weighted) distribution, purchase intent,
 * competitor mentions and the videos the comments came from.
 */
const PlatformSentimentDetail = ({ platformId, platformData, fullData }) => {
    const [weighted, setWeighted] = useState(false)

    const sentiment = platformData.sentiment || {}
    const analysis = platformData.analysis || {}
    const themes = Array.isArray(analysis.themes) ? analysis.themes : []
    const quotes = Array.isArray(analysis.notable_quotes) ? analysis.notable_quotes : []
    const competitors = Array.isArray(analysis.competitor_mentions) ? analysis.competitor_mentions : []
    const videos = Array.isArray(platformData.videos) ? platformData.videos : []
    const videoIndex = useMemo(() => buildVideoIndex(platformData), [platformData])
    const comments = useMemo(() => extractComments(fullData, platformId), [fullData, platformId])
    const maxMentions = themes.reduce((max, t) => Math.max(max, Number(t.mentions) || 0), 0)
    const hasWeighted = !!sentiment.weighted_percent && hasAnyCounts({ percent: sentiment.weighted_percent })

    const shownPercent = weighted && hasWeighted ? sentiment.weighted_percent : sentiment.percent
    const shownScore = weighted && hasWeighted ? sentiment.weighted_score : sentiment.score

    return (
        <div className="ugc-sent-detail-grid">
            {/* ── Main column ─────────────────────────────────────── */}
            <div className="d-flex flex-column gap-4" style={{ minWidth: 0 }}>
                {analysis.summary && (
                    <div>
                        <SectionTitle icon={Sparkles}>What the audience is saying</SectionTitle>
                        <p style={{ color: "#d1d5db", fontSize: "14.5px", lineHeight: 1.75, margin: 0 }}>{analysis.summary}</p>
                    </div>
                )}

                {themes.length > 0 && (
                    <div>
                        <SectionTitle icon={Layers}>Key themes</SectionTitle>
                        <div className="d-flex flex-column gap-2">
                            {[...themes]
                                .sort((a, b) => (Number(b.mentions) || 0) - (Number(a.mentions) || 0))
                                .map((theme, i) => (
                                    <ThemeCard key={i} theme={theme} maxMentions={maxMentions} videoIndex={videoIndex} platform={platformId} />
                                ))}
                        </div>
                    </div>
                )}

                <div className="ugc-sent-insight-grid">
                    {INSIGHT_GROUPS.map(group => (
                        <InsightGroup key={group.key} group={group} items={normalizeInsightList(analysis[group.key])} />
                    ))}
                </div>

                {quotes.length > 0 && (
                    <div>
                        <SectionTitle icon={Quote}>Notable quotes</SectionTitle>
                        <div className="ugc-sent-quote-grid">
                            {quotes.map((q, i) => {
                                const meta = SENTIMENT_META[q.sentiment] || SENTIMENT_META.unknown
                                return (
                                    <div key={i} className="ugc-sent-quote" style={{ borderTop: `2px solid ${meta.color}` }}>
                                        <Quote size={18} color="rgba(255,255,255,0.15)" />
                                        <p style={{ color: "#e8e8e8", fontSize: "14px", lineHeight: 1.6, margin: "8px 0 10px", wordBreak: "break-word" }}>{q.text}</p>
                                        {q.why && <p style={{ color: "#9ca3af", fontSize: "12.5px", lineHeight: 1.5, margin: "0 0 10px", fontStyle: "italic" }}>{q.why}</p>}
                                        <div className="d-flex align-items-center gap-3 flex-wrap mt-auto">
                                            <SentimentChip sentiment={q.sentiment} size="sm" />
                                            <Likes value={q.likes} />
                                            <VideoRef video={videoIndex[q.video_id]} platform={platformId} />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {comments.length > 0 && (
                    <div>
                        <SectionTitle icon={MessageCircle}>Analysed comments</SectionTitle>
                        <CommentsExplorer comments={comments} videoIndex={videoIndex} platform={platformId} />
                    </div>
                )}
            </div>

            {/* ── Sidebar column ──────────────────────────────────── */}
            <div className="d-flex flex-column gap-3" style={{ minWidth: 0 }}>
                <Card>
                    <SectionTitle icon={Gauge}>{getPlatformMeta(platformId).label} sentiment</SectionTitle>
                    {hasWeighted && (
                        <div className="ugc-sent-toggle mb-3" role="tablist">
                            <button type="button" className={!weighted ? "is-active" : ""} onClick={() => setWeighted(false)}>By comment</button>
                            <button type="button" className={weighted ? "is-active" : ""} onClick={() => setWeighted(true)}>Weighted by likes</button>
                        </div>
                    )}
                    <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                        <NetScore score={shownScore} size={34} />
                        <SentimentChip sentiment={platformData.overall_sentiment} />
                    </div>
                    <SentimentBar percent={shownPercent} counts={weighted ? {} : sentiment.counts} height={10} />
                    <div className="mt-3">
                        <SentimentLegend percent={shownPercent} counts={weighted ? {} : sentiment.counts} compact />
                    </div>
                    {weighted && (
                        <p style={{ color: "#6b7280", fontSize: "11.5px", margin: "10px 0 0", lineHeight: 1.5 }}>
                            Each comment counts as its likes + 1, so comments more people agreed with carry more weight.
                        </p>
                    )}
                </Card>

                <Card>
                    <SectionTitle icon={ShoppingCart}>Purchase intent</SectionTitle>
                    <PurchaseIntentMeter value={analysis.purchase_intent} />
                    <p style={{ color: "#6b7280", fontSize: "11.5px", margin: "10px 0 0", lineHeight: 1.5 }}>
                        How often commenters say they want to buy, download or play.
                    </p>
                </Card>

                <Card>
                    <SectionTitle icon={Swords}>Competitor mentions</SectionTitle>
                    {competitors.length === 0 ? (
                        <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>No competitors mentioned</p>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {competitors.map((c, i) => (
                                <div key={i}>
                                    <div style={{ color: "#fff", fontWeight: 600, fontSize: "13.5px" }}>{typeof c === "string" ? c : c?.name || "Unnamed"}</div>
                                    {c?.context &&<div style={{ color: "#9ca3af", fontSize: "12.5px", lineHeight: 1.5, marginTop: "2px" }}>{c.context}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                {videos.length > 0 && (
                    <Card>
                        <SectionTitle icon={Film}>{platformId === "instagram" ? "Reels" : "Videos"} analysed</SectionTitle>
                        <div className="d-flex flex-column">
                            {videos.map((video, i) => (
                                <a
                                    key={video.video_id || i}
                                    href={video.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ugc-sent-video-row"
                                    style={{ borderBottom: i < videos.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}
                                >
                                    <div className="ugc-clamp-2" style={{ color: "#e8e8e8", fontSize: "13px", fontWeight: 500, lineHeight: 1.45 }}>
                                        {video.title || `${getPlatformMeta(platformId).label} ${platformId === "instagram" ? "reel" : "video"}`}
                                        <ExternalLink size={11} style={{ marginLeft: "5px", opacity: 0.5 }} />
                                    </div>
                                    <div className="d-flex align-items-center gap-3 flex-wrap mt-1" style={{ fontSize: "11.5px", color: "#9ca3af" }}>
                                        {video.views != null && <span className="d-inline-flex align-items-center gap-1"><Eye size={11} /> {formatViews(video.views)}</span>}
                                        {video.comments_fetched != null && <span className="d-inline-flex align-items-center gap-1"><MessageCircle size={11} /> {video.comments_fetched}</span>}
                                        {formatSelectedAs(video.selected_as) && (
                                            <span style={{ background: "rgba(255,255,255,0.05)", padding: "1px 7px", borderRadius: "5px", color: "#adb5bd" }}>
                                                {formatSelectedAs(video.selected_as)}
                                            </span>
                                        )}
                                    </div>
                                </a>
                            ))}
                        </div>
                    </Card>
                )}

                <Card>
                    <SectionTitle icon={Info}>About this analysis</SectionTitle>
                    <div className="d-flex flex-column gap-2" style={{ fontSize: "13px", color: "#d1d5db" }}>
                        <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Platform</span><PlatformBadge id={platformId} /></div>
                        {platformData.comments_found != null && (
                            <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Comments found</span><span>{platformData.comments_found}</span></div>
                        )}
                        {platformData.comments_analysed != null && (
                            <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Top comments analysed</span><span>{platformData.comments_analysed}</span></div>
                        )}
                        {formatSentimentDate(platformData.generated_at) && (
                            <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Generated</span><span>{formatSentimentDate(platformData.generated_at)}</span></div>
                        )}
                        {formatSentimentDate(platformData.expires_at) && (
                            <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Refreshes</span><span>{formatSentimentDate(platformData.expires_at)}</span></div>
                        )}
                    </div>
                    <p style={{ color: "#6b7280", fontSize: "11.5px", margin: "12px 0 0", lineHeight: 1.5 }}>
                        Top comments are ranked by likes + 2 × replies. Replies and duplicates are excluded.
                    </p>
                </Card>
            </div>
        </div>
    )
}

export default PlatformSentimentDetail
